/**
 * What the Edit model dialog's Backing picker saves (TRA-1383). A model is
 * backed by a public catalog model or by the team's own deployment, and the
 * picker moves it in any direction. Pure module so the form file stays
 * fast-refreshable and the rules are testable without React.
 */

import type {
  CustomEndpointCreate,
  ForwardsTo,
  ModelUpdate,
  ScalingCopyResult,
} from '@/api/generated/model'

/** The picker value for "your own deployment"; every other value is a catalog slug. */
export const CUSTOM_BACKING = 'custom'

export type BackingPatch = { catalog_slug: string } | { custom: CustomEndpointCreate }

/** The picker value that matches where the model forwards today. */
export function currentBacking(forwardsTo: ForwardsTo): string {
  return forwardsTo.catalog_slug ?? CUSTOM_BACKING
}

/**
 * True when the typed deployment is the one the model already forwards to.
 * The gateway does not report Baseten ids, so they are not compared: a
 * deployment with the same upstream, endpoint and provider model is the same
 * slot either way.
 */
export function isSameDeployment(
  current: ForwardsTo,
  fields: Pick<CustomEndpointCreate, 'upstream_id' | 'base_url' | 'provider_model'>,
): boolean {
  return (
    current.catalog_slug === null &&
    current.upstream_id === fields.upstream_id &&
    current.endpoint_url === fields.base_url &&
    current.provider_model === fields.provider_model
  )
}

/**
 * The backing fields of the save, or null when the backing is unchanged.
 * `customFields` is null while the custom fields are incomplete, so a
 * half-typed deployment never reaches the wire.
 */
export function backingPatch(
  picked: string,
  current: ForwardsTo,
  customFields: CustomEndpointCreate | null,
): BackingPatch | null {
  if (picked !== CUSTOM_BACKING) {
    return picked === current.catalog_slug ? null : { catalog_slug: picked }
  }
  if (customFields === null || isSameDeployment(current, customFields)) return null
  return { custom: customFields }
}

/**
 * The primary route the save leaves in place, which the chain must not
 * repeat: the picked catalog slug, the custom upstream once its fields are
 * complete (`null` before that), else the stored primary.
 */
export function effectivePrimaryFor(
  picked: string,
  customUpstreamId: string | null,
  storedPrimary: string,
): string {
  if (picked !== CUSTOM_BACKING) return picked
  return customUpstreamId ?? storedPrimary
}

/** An upstream and how the gateway probes it (`healthCheck.type`). */
export interface UpstreamKind {
  id: string
  healthCheckType: string | null
}

/**
 * True when the save moves a dedicated Baseten deployment onto another one,
 * so the dialog can offer to copy its schedule windows and replica range
 * (TRA-1422). A catalog backing has no deployment of ours to read, and an
 * upstream the gateway does not probe through Baseten has no scaling to
 * copy, so the offer needs a custom slot on a Baseten-probed upstream on
 * both sides.
 */
export function canCopyScaling(
  patch: BackingPatch | null,
  current: ForwardsTo,
  upstreams: readonly UpstreamKind[],
): boolean {
  if (patch === null || !('custom' in patch) || current.catalog_slug !== null) return false
  const kindOf = (id: string | null): string | null =>
    upstreams.find((u) => u.id === id)?.healthCheckType ?? null
  return kindOf(current.upstream_id) === 'baseten' && kindOf(patch.custom.upstream_id) === 'baseten'
}

/** The copy-scaling box's label: what travels, and that the schedule is replaced. */
export const COPY_SCALING_LABEL =
  'Also copy the scaling settings from the current deployment: schedule, min/max replicas, ' +
  'scale-down delay and the other autoscaling settings. ' +
  "This replaces the new deployment's schedule."

/** What the save's toast says about the scaling copy, and whether it is good news. */
export interface ScalingCopyNotice {
  status: ScalingCopyResult['status']
  text: string
}

/**
 * The toast after a save that asked to copy the scaling (TRA-1422). The
 * model moved either way; a failed copy names where to set the scaling by
 * hand, which is the model page, since the dialog has no scaling tab.
 */
export function scalingCopyNotice(result: ScalingCopyResult): ScalingCopyNotice {
  if (result.status === 'copied') {
    return { status: 'copied', text: 'Scaling copied to the new deployment.' }
  }
  return {
    status: 'failed',
    text:
      `Moved. The scaling settings were not copied: ${result.message}. ` +
      'Set them under Replicas over time and Scheduled replicas on the model page.',
  }
}

export interface ModelSave {
  slug: string
  display_name: string
  /** The backing fields, or null when the pick keeps the backing. */
  backing: BackingPatch | null
  /** The chain to store, or null when this dialog did not change it. */
  fallbacks: string[] | null
  /** A billing warning (display-only or hourly) is on screen and its box is ticked. */
  dropBillingEntry: boolean
  /** The scaling copy is offered and its box is ticked. */
  copyScaling: boolean
}

/**
 * The PATCH body of an Edit model save. A directive rides along only when
 * it applies: the chain only when edited here, so a concurrent editor's
 * chain is not stomped by a metadata save; the two boxes only when ticked,
 * and only with the backing change they belong to.
 */
export function modelUpdateBody(save: ModelSave): ModelUpdate {
  return {
    slug: save.slug,
    display_name: save.display_name,
    ...save.backing,
    ...(save.fallbacks === null ? {} : { fallbacks: save.fallbacks }),
    ...(save.dropBillingEntry ? { drop_billing_entry: true } : {}),
    ...(save.copyScaling ? { copy_scaling: true } : {}),
  }
}
